import { useSearchParams, Link as RouterLink } from 'react-router-dom'
import { Box, Stack, TableBody, TableHead, TableRow, Typography } from '@mui/material'

import { Button, Card, Skeleton } from '@repo/ui'
import { SelectField } from 'components'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useCompetitorSeo } from 'hooks/api'
import { ROUTES } from 'routes'

import { Cell, EmptyRow, HeadCell, SectionNote, StatTable, dateTime } from '../InternalAnalyticsPage/components/shared'
import { CompetitorLink } from './CompetitorLink'

/**
 * VRG-744 — the SEO tab: per competitor, how its search footprint overlaps Vera's, read straight
 * from the SEO tables (the latest completed result page per tracked keyword, and the gap finder's
 * pulled keywords). A view, not a copy: GTM → SEO's Rankings and Gap finder stay where the data is
 * worked, and this tab links to them.
 */
export const CompetitorSeoTab = () => {
  const [params, setParams] = useSearchParams()
  const market = params.get('market') ?? 'us'
  const { data, isLoading, error } = useCompetitorSeo(market)

  const setMarket = (m: string) => {
    const next = new URLSearchParams(params)
    next.set('market', m)
    setParams(next, { replace: true })
  }

  if (isLoading)
    return (
      <Card>
        <Skeleton height={14} lines={8} />
      </Card>
    )
  if (error || !data)
    return (
      <Card>
        <SectionNote>{(error as Error | null)?.message ?? 'Could not load competitor SEO.'}</SectionNote>
      </Card>
    )

  const seo = (section: string) => `${ROUTES.INTERNAL_GTM_SEO}?seo=${section}&market=${data.market}`

  return (
    <Card
      title="Search overlap"
      action={
        <Stack direction="row" spacing={3}>
          <Button variant="secondary" component={RouterLink} to={seo('gaps')}>
            Gap finder
          </Button>
          <Button variant="secondary" component={RouterLink} to={seo('rankings')}>
            Rankings
          </Button>
        </Stack>
      }
    >
      <Stack spacing={4} sx={{ mt: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <SectionNote>
            {data.keywordsChecked
              ? `From the latest result page for each of ${data.keywordsChecked} tracked keywords in this market (last check ${dateTime(data.lastCheckAt)}), and the keywords pulled for each competitor in the gap finder.`
              : 'No completed ranking checks in this market yet. Rankings fill in after the next scheduled check.'}
          </SectionNote>
          {data.markets.length > 1 ? (
            <Box sx={{ minWidth: 220 }}>
              <SelectField
                value={data.market}
                onChange={(v) => setMarket(v as string)}
                options={data.markets.map((m) => ({ value: m.code, label: m.name }))}
              />
            </Box>
          ) : null}
        </Stack>
        <StatTable>
          <TableHead>
            <TableRow>
              <HeadCell>Competitor</HeadCell>
              <HeadCell align="right">
                <InfoTooltip text="Tracked keywords whose latest result page shows this competitor.">
                  <span style={{ cursor: 'help' }}>Ranks for</span>
                </InfoTooltip>
              </HeadCell>
              <HeadCell align="right">
                <InfoTooltip text="Of those, keywords where Vera is on the same result page.">
                  <span style={{ cursor: 'help' }}>Shared with Vera</span>
                </InfoTooltip>
              </HeadCell>
              <HeadCell align="right">
                <InfoTooltip text="Of those, keywords where this competitor is placed above Vera, or Vera is not on the page.">
                  <span style={{ cursor: 'help' }}>Above Vera</span>
                </InfoTooltip>
              </HeadCell>
              <HeadCell align="right">Avg position</HeadCell>
              <HeadCell align="right">Gap keywords</HeadCell>
              <HeadCell>Top gap keywords</HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.length === 0 ? (
              <EmptyRow colSpan={7} text="No approved competitors." />
            ) : (
              data.rows.map((r) => (
                <TableRow key={r.domain}>
                  <Cell strong>
                    <CompetitorLink domain={r.domain} name={r.name} strong />
                  </Cell>
                  <Cell align="right">{r.rankedKeywords}</Cell>
                  <Cell align="right">{r.sharedWithVera}</Cell>
                  <Cell align="right">{r.aheadOfVera}</Cell>
                  <Cell align="right">{r.avgPosition ?? '—'}</Cell>
                  <Cell align="right">{r.gapKeywords}</Cell>
                  <Cell>
                    <Box sx={{ whiteSpace: 'normal', minWidth: 220, maxWidth: 420, lineHeight: 1.5 }}>
                      {r.topGapKeywords.length ? (
                        r.topGapKeywords.map((k) => (
                          <Typography key={k.keyword} variant="body3" component="div">
                            {k.keyword}
                            <Typography component="span" variant="body3" color="text.secondary">
                              {k.volume !== null ? ` · ${k.volume.toLocaleString()}/mo` : ''}
                              {k.position !== null ? ` · #${k.position}` : ''}
                            </Typography>
                          </Typography>
                        ))
                      ) : (
                        '—'
                      )}
                    </Box>
                  </Cell>
                </TableRow>
              ))
            )}
          </TableBody>
        </StatTable>
      </Stack>
    </Card>
  )
}
